<template>
  <div class="container-fluid">
    <div class="row">
      <div class="col-3">
        <search-bar @listen-search="listenSearchEvent" />
      </div>
      <div class="col-9">
        <div class="row">
          <vue-good-table
            class="vue-table"
            ref="table"
            mode="remote"
            @page-change="onPageChange"
            @sort-change="onSortChange"
            @column-filter="onColumnFilter"
            @per-page-change="onPerPageChange"
            :isLoading.sync="isLoading"
            :pagination-options="paginationOptions"
            :totalRows="typesenseStore.totalRecords"
            :rows="typesenseStore.packageList"
            :columns="columns"
            ><template #table-actions-bottom>
              <pagination-records
                :pages="serverParams.page"
                :per-page="serverParams.per_page"
                :total="typesenseStore.totalRecords"
              />
            </template>
            <template #table-row="props">
              <span v-if="props.column.field == 'fixity_check_passed'">
                <span v-if="props.row.fixity_check_passed == null">-</span>
                <span v-else-if="props.row.fixity_check_passed == true"
                  >Passed</span
                >
                <span v-else-if="props.row.fixity_check_passed == false"
                  >Failed</span
                >
                <span v-else>{{ props.formattedRow[props.column.field] }}</span>
              </span>
              <span v-if="props.column.field == 'fixity_checked_at'">
                <span v-if="props.row.fixity_checked_at == null">-</span>
                <span v-else>{{ props.formattedRow[props.column.field] }}</span>
              </span>
              <span v-if="props.column.field == 'transfer_date'">
                <span v-if="props.row.transfer_date == null">-</span>
                <span v-else>{{ props.formattedRow[props.column.field] }}</span>
              </span>
              <span v-if="props.column.field == 'after'">
                <button 
                  class="btn btn-icon" 
                  v-tooltip="'View Details'" 
                  @click="viewDetails($event, props.row.uuid)"
                >
                  <i class="material-icons details">open_in_new</i>
                </button>
                <button 
                  class="btn btn-icon" 
                  v-tooltip="'Request Download'"
                  data-bs-toggle="modal"
                  data-bs-target="#singledownloadConfirmationModal"
                  @click="requestDownload($event, props.row.uuid, props.row.status)"
                >
                  <i class="material-icons download">download</i>
                </button>
                <button 
                  class="btn btn-icon" 
                  v-tooltip="'Resubmit Package'" 
                  data-bs-toggle="modal"
                  data-bs-target="#resubmitPackageModal"
                  @click="resubmitPackage($event, props.row.sip_uuid, props.row.status, props.row.type)"
                > 
                  <i class="material-icons resubmit">rotate_right</i>
                </button>
              </span>
            </template>
          </vue-good-table>
        </div>
      </div>
    </div>
  </div>
  <SingleDownloadAipDipModal :selected-id="selectedId" :selected-status="selectedStatus" />
  <resubmit-package-modal :selected-id="selectedSipId" :selected-status="selectedStatus" :selected-package-type="selectedPackageType" />
</template>

<script setup lang="ts">
import SingleDownloadAipDipModal from "@/components/Modal/SingleDownloadAipDipModal.vue";
import ResubmitPackageModal from "@/components/Modal/ResubmitPackageModal.vue";
import PaginationRecords from "@/components/Pagination/PaginationRecords.vue";
import SearchBar from "@/components/Search/SearchBar.vue";
import { useTypesenseStore } from "@/stores/typesense";
import { useAsyncState } from "@vueuse/core";
import { Tooltip } from "bootstrap";
import moment from "moment";
import { ref, watch } from "vue";
import { useRouter } from "vue-router";
import { useAuditStore } from "@/stores/audit";

const audit = useAuditStore();
const table = ref();
const typesenseStore = useTypesenseStore();
const router = useRouter();
const isLoading = ref(false);
const selectedId = ref();
const selectedSipId = ref();
const selectedStatus = ref();
const selectedPackageType = ref();
const type = ref("secure-zone-packages");

const closeTooltip = (event: MouseEvent) => {
  const target = event.target as HTMLElement; // Cast event target to HTMLElement
  const tooltipInstance = (target._tooltip as Tooltip) || null; // Safely get the tooltip instance

  if (tooltipInstance) {
    tooltipInstance.hide(); // Hide the tooltip if it's initialized
  } 
};

const viewDetails = (event: MouseEvent, uuid: string) => {
  closeTooltip(event);
  audit.auditLog("View");
  setTimeout(() => {
    router.push({
      name: 'aipdipdetails',
      params: { selectedSearchId: uuid },
    });
  }, 300); // Delay to ensure the tooltip has fully hidden before navigating
}

const requestDownload = (event: MouseEvent, uuid: string, status: string) => {
  closeTooltip(event)
  selectedId.value = uuid;
  selectedStatus.value = status;
};

const resubmitPackage = (event: MouseEvent, uuid: string, status: string, type: string) => {
  closeTooltip(event)
  selectedSipId.value = uuid;
  selectedStatus.value = status;
  selectedPackageType.value = type;
};

// Tooltip directive, to initialize and destroy Bootstrap tooltips
const tooltipDirective = {
  mounted(el: HTMLElement, binding: any) {
    const tooltip = new Tooltip(el, {
      title: binding.value || '', // Default value for title
      trigger: 'hover',           // Tooltip trigger on hover
      placement: 'top',           // Tooltip position
    });
    // Attach tooltip instance to the element for later reference
    (el as HTMLElement)._tooltip = tooltip;
  },
  beforeUnmount(el: HTMLElement) {
    if (el && (el as HTMLElement)._tooltip) {
      console.log('Disposing of tooltip on element', el);
      (el as HTMLElement)._tooltip.dispose();
    }
  },
};

watch(
  () => typesenseStore.loading,
  (updateLoading) => (isLoading.value = updateLoading)
);

const formatFn = (value) => {
  return moment.unix(value).format("DD MMM yyyy");
};

const formatDateTime = (value) => {
  return moment.unix(value).format("DD MMM yyyy HH:mm:ss");
};

const columns = [
  {
    label: "Transfer UUID",
    field: "transfer_uuid",
    sortable: false,
  },
  {
    label: "SIP  UUID",
    field: "sip_uuid",
    sortable: false,
  },
  {
    label: "Transfer Batch No",
    field: "transfer_batch",
  },
  {
    label: "Transferring Agency",
    field: "transfer_agency",
  },
  {
    label: "Date of Transfer",
    field: "transfer_date",
    formatFn: formatFn,
    width: "80px",
  },
  {
    label: "Status",
    field: "status",
    width: "70px",
    sortable: false,
  },
  {
    label: "Item ID",
    field: "item_id",
    sortable: false,
  },
  {
    label: "Package Type",
    field: "type",
    sortable: false,
  },
  {
    label: "AIP/DIP Version",
    field: "version",
    sortable: false,
  },
  {
    label: "Fixity Check",
    field: "fixity_check_passed",
    sortable: false,
  },
  {
    label: "Fixity Checked At",
    field: "fixity_checked_at",
    formatFn: formatDateTime,
    sortable: false,
  },
  {
    label: "",
    field: "after",
    sortable: false,
    width: "140px",
  },
];

const paginationOptions = ref({
  enabled: true,
  mode: "pages",
  position: "bottom",
  perPage: typesenseStore.serverParams.per_page,
  perPageDropdown: [10, 20, 30, 250],
  setCurrentPage: typesenseStore.serverParams.page,
  dropdownAllowAll: false,
});

const serverParams = ref({
  filter_by: typesenseStore.serverParams.filter_by,
  sort_by: typesenseStore.serverParams.sort_by,
  q: typesenseStore.serverParams.q,
  query_by: typesenseStore.serverParams.query_by,
  page: typesenseStore.serverParams.page,
  per_page: typesenseStore.serverParams.per_page,
});

const updateParams = (newProps: any) => {
  serverParams.value = Object.assign({}, serverParams.value, newProps);
};

const onPageChange = (params) => {
  typesenseStore.storePageDetails(params.currentPage, params.currentPerPage);
  updateParams({
    per_page: params.currentPerPage,
    page: params.currentPage,
  });
  execute();
};

const onPerPageChange = (params) => {
  typesenseStore.storePageDetails(1, params.currentPerPage);
  updateParams({ per_page: params.currentPerPage, page: 1 });
  execute();
};

const onSortChange = (params) => {
  if (params[0].type === "none") {
    updateParams({
      sort_by: "",
    });
  } else {
    updateParams({
      sort_by: params[0].field + ":" + params[0].type,
    });
  }
  execute();
};

const onColumnFilter = (params) => {
  table.value.reset();
  updateParams({ sort: params.sort, page: 1 });
  execute();
};

const listenSearchEvent = (inputParams) => {
  let searchData: string = "";
  for (const [key, value] of Object.entries(inputParams)) {
    if (!key.includes("transfer_batch")) {
      if (value) searchData += `${key}:${value}&&`;
    } else {
      serverParams.value.q = value as string;
    }
  }
  serverParams.value.filter_by = searchData.slice(0, -2);
  typesenseStore.storeData(serverParams.value);
  onColumnFilter(typesenseStore.serverParams);
};

const { execute, error } = useAsyncState(() => {
  return typesenseStore.fetchTypesense(type.value, serverParams.value);
}, null);
</script>

<style scoped>
.vue-table {
  padding-top: 16px;
  padding-bottom: 16px;
}
.btn-icon {
  border: none;
  padding: 8px;
}
.btn:disabled {
  /*background:grey;*/
  color: grey;
  border: none !important;
}
.material-icons.details {
  font-size: 24px;
  color: #0d6efd;
}
.material-icons.download {
  color: #198754;
}
.material-icons.resubmit {
  color: #dc3545;
}
</style>
